import React, { useState, useCallback, useEffect } from 'react';
import './GameRoom.css'; // We'll create this for styling
import { startGame, playCard, drawCard } from '../services/api'; // Import startGame, playCard, and drawCard
import Card from './Card'; // Import the Card component
import './Card.css'; // Assuming Card.css is in the same directory or adjust path
import useIsMobile from '../hooks/useIsMobile'; // Import the custom hook

// Placeholder for Avatar component - to be created later
const Avatar = ({ username, isCurrentUser, isTurn }) => (
  <div className={`player-avatar ${isCurrentUser ? 'current-user-avatar' : ''} ${isTurn ? 'current-turn-highlight' : ''}`}>
    <div className="avatar-circle">{username ? username.substring(0, 1).toUpperCase() : 'U'}</div>
    <p className="player-name">{username || 'Player'}</p>
  </div>
);

// Placeholder GameRoom component
function GameRoom({ gameRoomData, currentUser, onLeaveRoom }) {
  // ADDED CONSOLE LOGS
  console.log('[GameRoom.js] Rendering or re-rendering. Full gameRoomData prop:', JSON.stringify(gameRoomData));
  console.log('[GameRoom.js] gameRoomData.players upon render:', JSON.stringify(gameRoomData?.players));
  console.log('[GameRoom.js] gameRoomData.game_room (roomDetails) upon render:', JSON.stringify(gameRoomData?.game_room));

  const [copyButtonText, setCopyButtonText] = useState('Copy Room Code');
  const [error, setError] = useState('');
  const [isChoosingColor, setIsChoosingColor] = useState(false);
  const [cardToPlay, setCardToPlay] = useState(null);
  const [drawError, setDrawError] = useState('');
  const [tappedCardKey, setTappedCardKey] = useState(null);
  const isMobile = useIsMobile(); // Use the hook
  const [isStartingGame, setIsStartingGame] = useState(false); // Added state for disabling start button

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => { setError(''); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (drawError) {
      const timer = setTimeout(() => { setDrawError(''); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [drawError]);

  // Derive dependencies for the tappedCardKey effect at the top level
  const turnPlayerIdFromData = gameRoomData?.game_room?.turn_player_id;
  // Need to find the current player from gameRoomData.players to get hand length safely
  const currentPlayerFromData = gameRoomData?.players?.find(p => p.user_id === currentUser?.id);
  const currentHandLengthFromData = currentPlayerFromData?.hand?.length;

  // useEffect for clearing tappedCardKey now at the top level
  useEffect(() => {
    setTappedCardKey(null);
  }, [turnPlayerIdFromData, currentHandLengthFromData]);

  // Memoize handlers that don't depend on rapidly changing state
  // MUST be defined before any early returns.
  const handleCopyRoomCode = useCallback(() => {
    if (gameRoomData && gameRoomData.game_room && gameRoomData.game_room.code) {
      navigator.clipboard.writeText(gameRoomData.game_room.code)
        .then(() => {
          setCopyButtonText('Copied!');
          setTimeout(() => setCopyButtonText('Copy Room Code'), 2000);
        })
        .catch(err => {
          console.error('Failed to copy room code: ', err);
          setCopyButtonText('Failed to copy');
          setTimeout(() => setCopyButtonText('Copy Room Code'), 2000);
        });
    }
  }, [gameRoomData]);

  const handleStartGame = useCallback(async () => {
    if (!gameRoomData || !gameRoomData.game_room || !gameRoomData.game_room.code || !currentUser || !currentUser.id) {
      setError('Cannot start game: Missing game or user information.');
      return;
    }
    if (isStartingGame) return; // Prevent multiple calls if already starting

    setIsStartingGame(true);
    setError('');
    try {
      await startGame(gameRoomData.game_room.code, currentUser.id);
      // If successful, ActionCable 'game_started' will update the state.
      // The button should disappear because roomDetails.status will no longer be 'waiting'.
      // So, we don't necessarily need to set isStartingGame to false here on success.
    } catch (err) {
      setError(err.message || 'Failed to start game. Please try again.');
      setIsStartingGame(false); // Only set back to false on error, allowing another attempt.
    }
    // Removed setIsStartingGame(false) from here to make disabling more sticky until state change or error.
  }, [gameRoomData, currentUser, isStartingGame]);

  // Early return if gameRoomData is not yet loaded
  if (!gameRoomData || !gameRoomData.game_room) {
    return <div className="loading-screen">Loading game room data...</div>;
  }

  // Destructure after the loading check
  const { 
    game_room: roomDetails,
    players: players = [], // Changed back from 'player_list' to 'players'
    top_card,
    draw_pile_size 
  } = gameRoomData;

  const currentPlayer = players.find(p => p.user_id === currentUser?.id);
  const opponents = players.filter(p => p.user_id !== currentUser?.id);
  
  // Layout for opponents: Can be adjusted for more players later
  const opponentSlots = [
    { position: 'top-left', player: opponents.find(p => p.position === 1) }, // Example: map based on actual positions or order
    { position: 'top-center', player: opponents.find(p => p.position === 2) },
    { position: 'top-right', player: opponents.find(p => p.position === 3) },
  ];
  // For a 2-player game (1 opponent), let's place them top-center
  // For a 3-player game (2 opponents), top-left and top-right
  // For a 4-player game (3 opponents), top-left, top-center, top-right
  let displayedOpponents = [];
  if (opponents.length === 1) {
    displayedOpponents = [{ positionClass: 'opponent-center', player: opponents[0] }];
  } else if (opponents.length === 2) {
    displayedOpponents = [
      { positionClass: 'opponent-left', player: opponents[0] },
      { positionClass: 'opponent-right', player: opponents[1] }
    ];
  } else if (opponents.length === 3) {
    displayedOpponents = [
      { positionClass: 'opponent-left', player: opponents[0] },
      { positionClass: 'opponent-center', player: opponents[1] },
      { positionClass: 'opponent-right', player: opponents[2] }
    ];
  }

  const isMyTurn = currentPlayer && currentPlayer.id === roomDetails.turn_player_id;

  const attemptPlayCardLogic = (cardToAttempt) => {
    if (cardToAttempt.card_type === 'wild' || cardToAttempt.card_type === 'wild_draw_four') {
      setCardToPlay(cardToAttempt);
      setIsChoosingColor(true);
    } else {
      executePlayCard(cardToAttempt, null);
    }
    // tappedCardKey will be reset by the calling function (handleCardTap or direct play on desktop)
  };

  const handleCardInteraction = (card, cardKey) => {
    if (!isMyTurn) {
      setError("It's not your turn!");
      if (isMobile) setTappedCardKey(null); // Clear selection if mobile and not their turn
      return;
    }

    if (isMobile) {
      // Mobile: Two-tap logic
      if (tappedCardKey === cardKey) { // Second tap on the same card
        attemptPlayCardLogic(card);
        setTappedCardKey(null); // Reset after initiating play or color choice
      } else { // First tap on this card, or tap on a different card
        setTappedCardKey(cardKey);
      }
    } else {
      // Desktop: Single-click logic
      attemptPlayCardLogic(card);
      // No need to manage tappedCardKey for desktop as play is immediate
    }
  };

  const handleColorChoice = (color) => {
    if (cardToPlay) {
      executePlayCard(cardToPlay, color);
    }
    setIsChoosingColor(false);
    setCardToPlay(null);
    if (isMobile) setTappedCardKey(null); // Clear selection after color choice on mobile
  };

  const executePlayCard = async (card, chosenColor) => {
    if (!roomDetails || !currentUser) return;
    try {
      // setError(''); // Error is cleared by useEffect now
      const result = await playCard(roomDetails.code, currentUser.id, card, chosenColor);
      console.log('Card played API result:', result);
      if (result && result.error) {
        setError(result.error);
      }
      // Note: After a successful play, the hand will change, triggering the useEffect to clear tappedCardKey.
    } catch (err) {
      setError(err.message || 'Failed to play card. Please try again.');
    }
  };

  const handleDrawCard = async () => {
    console.log('[GameRoom.js] handleDrawCard triggered.');
    console.log('[GameRoom.js] isMyTurn:', isMyTurn, 'draw_pile_size:', draw_pile_size);

    if (!isMyTurn) {
      setDrawError("It's not your turn!");
      return;
    }
    if (!roomDetails || !currentUser) return;

    if (isMobile) setTappedCardKey(null); // Drawing a card should clear any tapped card selection on mobile
    try {
      setDrawError(''); // Clear previous draw errors
      const result = await drawCard(roomDetails.code, currentUser.id);
      console.log('Card drawn API result:', result);
      // Action Cable will broadcast the card_drawn and subsequent game_state update.
      // If result.can_play is true, the player might be able to play the drawn_card.
      // For now, we rely on Action Cable to update the hand and game state.
      // A more advanced UI could highlight the drawn card and allow playing it if result.can_play is true.
      if (result && result.error) { // If backend returns specific error for the draw
        setDrawError(result.error);
      }
    } catch (err) {
      setDrawError(err.message || 'Failed to draw card. Please try again.');
    }
  };

  // Determine whose turn it is
  const turnPlayer = players.find(p => p.id === roomDetails.turn_player_id);
  const turnPlayerUsername = turnPlayer ? turnPlayer.username : (roomDetails.status !== 'waiting' ? 'N/A' : 'Game not started');

  // Color Picker Modal/Component (simple version for now)
  const ColorPicker = () => (
    <div className="color-picker-modal">
      <h4>Choose a Color:</h4>
      <div className="color-options">
        {['red', 'blue', 'green', 'yellow'].map(color => (
          <button 
            key={color} 
            className={`color-choice-button ${color}`}
            onClick={() => handleColorChoice(color)}
          >
            {color.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );

  // Helper to generate styles for stacked cards
  const getStackCardStyle = (index, total) => {
    // Simplified stacking: only a slight offset for cards underneath the top one.
    // No rotation to reduce complexity that might conflict with animation.
    const xOffset = index * 2; // Small horizontal offset for underlying cards
    const yOffset = index * 2; // Small vertical offset for underlying cards
    return {
      // The animation will control the transform of the top card to its final resting place.
      // Underlying cards get a simple translational offset.
      transform: `translate(${xOffset}px, ${yOffset}px)`,
      zIndex: index,
      position: 'absolute', // Important for stacking
      bottom: 0, // Align at the bottom of the pile container
      left: 0, // Align at the left of the pile container
    };
  };

  // === RENDER LOGIC ===
  if (roomDetails.status === 'waiting') {
    return (
      <div className="gameroom-container waiting-room-lobby">
        <div className="room-header">
          <h1>Room Code: {roomDetails.code}</h1>
          <button onClick={handleCopyRoomCode} className="action-button copy-room-button">
            {copyButtonText}
          </button>
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="players-list-waiting">
          <h3>Players ({players.length} / 4):</h3>
          <ul>
            {players.map(player => (
              <li key={player.id || player.user_id}>
                {player.username} {player.user_id === currentUser?.id ? '(You)' : ''}
              </li>
            ))}
          </ul>
        </div>
        {currentUser?.id === players[0]?.user_id && roomDetails.can_start && (
          <button 
            onClick={handleStartGame} 
            className="action-button start-game-button"
            disabled={isStartingGame} // Disable button while starting
          >
            {isStartingGame ? 'Starting...' : `Start Game (${players.length}/2-4)`}
          </button>
        )}
         <button onClick={onLeaveRoom} className="action-button leave-room-button">Leave Room</button>
      </div>
    );
  }

  return (
    <div className="gameroom-container active-game-board">
      {isChoosingColor && <ColorPicker />}
      {error && <p className="error-message game-error-message">{error}</p>}
      {drawError && <p className="error-message draw-error-message">{drawError}</p>}

      <div className="top-opponent-area">
        {displayedOpponents.map(opSlot => 
          opSlot.player ? (
            <div key={opSlot.player.id} className={`opponent-slot ${opSlot.positionClass} ${opSlot.player.id === roomDetails.turn_player_id ? 'is-their-turn-slot' : ''}`}>
              <Avatar 
                username={opSlot.player.username} 
                isTurn={opSlot.player.id === roomDetails.turn_player_id}
              />
              <div className="opponent-hand-display">
                {/* Display a few card backs to represent hand, count can be shown by Avatar or text */}
                {Array.from({ length: Math.min(opSlot.player.hand_size, 3) }).map((_, i) => (
                    <div key={i} className="card-back-wrapper" style={{ marginLeft: i > 0 ? '-55px' : '0', zIndex: i }}>
                        <Card card={{ isBack: true, value: '' }} />
                    </div>
                ))}
                {opSlot.player.hand_size > 3 && <span className="more-cards-indicator">+{opSlot.player.hand_size - 3}</span>}
                 {opSlot.player.hand_size === 0 && opSlot.player.id !== currentPlayer?.id && <span className="no-cards-indicator">No Cards!</span>}
              </div>
              <p className="opponent-card-count">{opSlot.player.hand_size} cards</p>
              {opSlot.player.id === roomDetails.turn_player_id && <p className="opponent-turn-indicator animated-indicator">{opSlot.player.username}'s Turn</p>}
            </div>
          ) : (
            <div key={opSlot.positionClass} className={`opponent-slot ${opSlot.positionClass} empty-slot`}></div> // Empty slot placeholder
          )
        )}
      </div>

      {/* Turn Indicators - Re-added, placement will be refined by CSS */}
      {/* <div className="turn-indicators">
        TODO: Add arrow SVGs and logic for direction
        <span className={`arrow left-arrow ${roomDetails.direction === 'counter_clockwise' ? 'active' : ''}`}>&larr;</span>
        <span className={`arrow right-arrow ${roomDetails.direction === 'clockwise' ? 'active' : ''}`}>&rarr;</span>
      </div> */}

      {/* Center Table Area */}
      <div className="center-table-area">
        <div className="draw-pile-area game-pile">
          <div className="card-stack-visual">
            {draw_pile_size > 0 ? (
              Array.from({ length: Math.min(draw_pile_size, 3) }).map((_, index) => (
                <div key={`draw-${index}`} className="pile-card-wrapper" style={getStackCardStyle(index, Math.min(draw_pile_size, 3))}>
                  <Card card={{ isBack: true, value: 'UNO' }} />
                </div>
              ))
            ) : (
              <div className="pile-card-wrapper empty-pile">
                 <Card card={{ isEmptyPile: true, value: 'Empty'}} /> {/* Special state for empty pile card */}
              </div>
            )}
          </div>
          <span className="pile-count-badge">{draw_pile_size}</span>
          <button 
            onClick={handleDrawCard} 
            disabled={!isMyTurn || draw_pile_size === 0} 
            className="action-button draw-action-button"
          >
            Draw
          </button>
        </div>

        <div className="discard-pile-area game-pile">
          <div className="card-stack-visual">
            {top_card ? (
              Array.from({ length: Math.min(roomDetails.discard_pile_size || 1, 3) }).map((_, index, arr) => {
                const isTopVisualCard = index === arr.length - 1;
                const cardToRender = isTopVisualCard ? top_card : { ...top_card, value: '' }; // Render actual top_card only once visually on top
                // Use a key that changes when the actual top_card changes to trigger re-render/animation
                const cardKey = isTopVisualCard ? `${top_card.color}-${top_card.value}-${top_card.card_type}-${roomDetails.discard_pile_size}` : `discard-dummy-${index}`;

                return (
                  <div 
                    key={cardKey} 
                    className={`pile-card-wrapper ${isTopVisualCard ? 'animate-discard-arrival' : ''}`}
                    // For the top card that animates, its final transform comes from the animation.
                    // For cards underneath, apply the stacking style.
                    // The animated card needs basic positioning and z-index.
                    style={isTopVisualCard ? { position: 'absolute', bottom: 0, left: 0, zIndex: arr.length } : getStackCardStyle(index, arr.length)}
                  >
                    <Card card={cardToRender} /> 
                  </div>
                );
              })
            ) : (
              <div className="pile-card-wrapper empty-pile">
                <Card card={{ isEmptyPile: true, value: 'Empty'}} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Player Area */}
      <div className="current-player-zone">
        <div className="current-player-info">
            <Avatar 
              username={currentPlayer?.username} 
              isCurrentUser={true} 
              isTurn={isMyTurn}
            />
            {isMyTurn && <p className="your-turn-indicator animated-indicator">Your Turn!</p>}
            <p>Color: {roomDetails.current_color || 'Any'}</p>
        </div>
        <div className="current-player-hand">
          {currentPlayer && currentPlayer.hand && currentPlayer.hand.map((card, index) => {
            const cardKey = `${card.card_type}-${card.color}-${card.value}-${index}`;
            return (
              <div 
                key={cardKey} 
                onClick={() => handleCardInteraction(card, cardKey)}
                className={`card-wrapper ${isMyTurn ? 'playable' : 'not-playable'} ${isMobile && tappedCardKey === cardKey ? 'is-tapped-for-play' : ''}`}
              >
                <Card card={card} />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Utility Buttons Area - Optional, can be part of player zone or separate */}
      <div className="game-actions-footer">
         <button onClick={onLeaveRoom} className="action-button leave-room-button">Leave Room</button>
         {/* Add other global game actions here if needed, e.g. game menu */}
      </div>

      {/* Game Over Message - To be styled like screenshot */}
      {roomDetails.status === 'finished' && (
        <div className="game-over-modal">
          <h2>
            {gameRoomData.winner 
              ? (gameRoomData.winner.user_id === currentUser?.id ? 'YOU WON!' : `${gameRoomData.winner.username} WON!`)
              : 'GAME FINISHED' /* Fallback if winner info is missing */}
          </h2>
          {/* Add score, options to play again or leave */}
        </div>
      )}

    </div>
  );
}

export default GameRoom;
